import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkle, Books, Spinner, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { folderOptions } from './folderTree';
import { dmsApi } from './dmsApi';

/** AIでまとめる — 索引を読ませて綴りの候補を出し、選んだ分だけ作る。
 *  AIの束ね方は外すことがあるので、作成前に必ず人が選ぶ。 */
export function AutoGroupDialog({ open, onClose, onCreated, folders }) {
  const [scope, setScope] = useState('all');
  const [hint, setHint] = useState('');
  const [job, setJob] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (open) { setJob(null); setPicked({}); setHint(''); setScope('all'); }
    return () => clearTimeout(timer.current);
  }, [open]);

  const poll = useCallback(async (jid) => {
    try {
      const r = await dmsApi.autoGroupStatus(jid);
      setJob(r);
      if (r.status === 'running') timer.current = setTimeout(() => poll(jid), 2500);
      else if (r.status === 'failed') toast.error(r.error || 'まとめに失敗しました');
      else if (!(r.groups || []).length) toast.info('まとめられる組が見つかりませんでした');
    } catch { toast.error('進捗の取得に失敗しました'); }
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const r = await dmsApi.autoGroupStart({
        folder_id: scope === 'all' ? null : scope, include_sub: true, hint,
      });
      setJob(r);
      poll(r.id);
    } catch (e) { toast.error(e?.response?.data?.detail || '開始できませんでした'); }
    finally { setBusy(false); }
  };

  const ids = Object.keys(picked).filter((k) => picked[k]);
  const create = async () => {
    setBusy(true);
    try {
      const r = await dmsApi.autoGroupCreate(job.id, ids);
      toast.success(`バインダー${r.created.length}冊を作成しました`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || '作成に失敗しました'); }
    finally { setBusy(false); }
  };

  const pct = job?.total ? Math.round((job.processed / job.total) * 100) : 0;
  const groups = job?.groups || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" aria-describedby={undefined} data-testid="auto-group-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Sparkle size={18} weight="duotone" className="text-teal-700" /> AIでまとめる
          </DialogTitle>
        </DialogHeader>
        {/* U3 — ページごとの分割は スキャナー受信箱 の1箇所だけ。ここからは案内する。 */}
        <p className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] text-zinc-600"
          data-testid="auto-group-split-hint">
          1つのPDFをページごとに分けたいときは
          <a href="/documents?view=scan" className="mx-1 font-semibold text-teal-700 underline">スキャナー受信箱</a>
          で分割してください（ここは既にある書類を束ねる機能です）
        </p>


        {!job && (
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">
              書類の題名・種類・図番・タグ・読み取った語をAIが読み、一緒に綴じるべき組を提案します。
              ファイルの中身そのものは送りません。作成するのは選んだ組だけです。
            </p>
            <div>
              <p className="text-xs font-semibold text-zinc-600 mb-1">対象</p>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="h-9 text-sm" data-testid="auto-group-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">見られるすべての書類</SelectItem>
                  {folderOptions(folders || []).map((o) => (
                    <SelectItem key={o.value} value={o.value} title={o.pathLabel}>
                      {o.label}（サブフォルダーも）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-600 mb-1">まとめ方の指示（任意）</p>
              <Textarea value={hint} onChange={(e) => setHint(e.target.value)} rows={3}
                data-testid="auto-group-hint"
                placeholder="例: 同じ注文番号の書類をまとめて / 装置ごとに図面と仕様書と検査成績書を1冊に / 見積と注文書は分けて"
                className="text-sm" />
              <p className="text-[10px] text-zinc-400 mt-1">
                指示があれば、AIはそれを最優先してまとめます。
              </p>
            </div>
          </div>
        )}

        {job && (
          <div className="space-y-3">
            <div>
              <p className="text-xs text-zinc-600 flex items-center gap-1.5" data-testid="auto-group-progress-text">
                {job.status === 'running' && <Spinner size={12} className="animate-spin text-teal-700" />}
                {job.status === 'done' && <CheckCircle size={12} weight="fill" className="text-emerald-600" />}
                {job.status === 'running' ? `読んでいます… ${job.processed}/${job.total}件`
                  : job.status === 'failed' ? 'まとめに失敗しました'
                    : `${job.total}件を読み、${groups.length}組の候補が見つかりました`}
              </p>
              <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden mt-2">
                <div className="h-full bg-teal-700 transition-[width] duration-500 ease-out"
                  style={{ width: `${pct}%` }} data-testid="auto-group-progress-bar" />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {groups.map((g) => (
                <label key={g.id} className="flex gap-2 rounded-md border border-zinc-200 p-2 hover:border-amber-400 cursor-pointer"
                  data-testid={`auto-group-row-${g.id}`}>
                  <input type="checkbox" checked={!!picked[g.id]} className="mt-0.5 h-4 w-4 accent-amber-600"
                    onChange={(e) => setPicked((p) => ({ ...p, [g.id]: e.target.checked }))} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <Books size={14} weight="duotone" className="text-amber-600 shrink-0" />
                      <span className="text-xs font-bold text-zinc-800 truncate">{g.name}</span>
                      <span className="text-[10px] text-zinc-400 shrink-0">{g.document_ids.length}件</span>
                    </span>
                    {g.reason && <span className="block text-[11px] text-zinc-500 mt-0.5">{g.reason}</span>}
                    <span className="block text-[10px] text-zinc-400 mt-0.5 line-clamp-2">
                      {g.member_titles.join(' / ')}
                    </span>
                    {(g.tags || []).length > 0 && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {g.tags.map((t) => (
                          <span key={t} className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[9px] text-zinc-600">{t}</span>
                        ))}
                      </span>
                    )}
                  </span>
                </label>
              ))}
              {job.status !== 'running' && groups.length === 0 && (
                <p className="text-xs text-zinc-400 py-6 text-center">まとめられる組は見つかりませんでした</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {job && groups.length > 0 && (
            <span className="mr-auto flex items-center gap-2">
              <span className="text-[11px] text-zinc-400">{ids.length}組 選択中</span>
              <button type="button" data-testid="auto-group-select-all"
                onClick={() => setPicked(Object.fromEntries(groups.map((g) => [g.id, true])))}
                className="text-[11px] text-teal-700 hover:underline">すべて選ぶ</button>
            </span>
          )}
          <Button variant="outline" onClick={onClose}>閉じる</Button>
          {!job ? (
            <Button onClick={start} disabled={busy} className="bg-teal-700 hover:bg-teal-800"
              data-testid="auto-group-start">
              {busy && <Spinner size={14} className="animate-spin mr-1.5" />}まとめを始める
            </Button>
          ) : (
            <Button onClick={create} disabled={busy || !ids.length}
              className="bg-amber-600 hover:bg-amber-700" data-testid="auto-group-create">
              選んだ{ids.length || ''}組を作成
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AutoGroupDialog;
